<template>
  <v-dialog
    v-model="innerValue"
    max-width="600"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi mdi-crane</v-icon>
        Nostoapuvälineiden tarkastus
      </v-card-title>
      <v-divider class="mb-2" />
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="partsForm.equipment"
              :items="equipmentList"
              item-text="displayName"
              return-object
              label="Valitse kalusto"
              :disabled="!hasEditPermissions"
              outlined
              hide-details
              :loading="loadingEquipments"
              clearable
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar v-if="item.pictureThumbnail">
                  <v-img :src="item.pictureThumbnail" contain></v-img>
                </v-list-item-avatar>
                <v-list-item-avatar v-else>
                  <v-icon>mdi-toy-brick</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title
                    >{{ item.id }} - {{ item.name }}</v-list-item-title
                  >
                  <v-list-item-subtitle>{{
                    item._fullTree
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item }">
                <v-avatar size="24" class="mr-2" v-if="item.pictureThumbnail">
                  <v-img :src="item.pictureThumbnail" contain></v-img>
                </v-avatar>
                <v-icon size="24" class="mr-2" v-else>mdi-toy-brick</v-icon>
                <span>{{ item.id }} - {{ item.name }}</span>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="!partsForm.equipment" class="mt-2">
          <v-col cols="12">
            <v-alert type="info" outlined dense>
              <v-icon left>mdi-information</v-icon>
              Valitse ensin tarkastettava nostoapuväline yllä olevasta valikosta
            </v-alert>
          </v-col>
        </v-row>
        <v-row class="mt-2"
          ><v-col cols="12" md="4">
            <modal-picker
              v-model="partsForm.date"
              label="Päivämäärä"
              :disabled="!hasEditPermissions || !partsForm.equipment"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="partsForm.inspector"
              label="Tarkastaja"
              :disabled="!hasEditPermissions || !partsForm.equipment"
              readonly
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="partsForm.worksite"
              label="Työpaikka/Sijainti"
              :disabled="!hasEditPermissions || !partsForm.equipment"
              outlined
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text
        v-if="partsForm.equipment && partsForm.date && partsForm.inspector"
      >
        <v-row>
          <v-col cols="12" v-if="partsForm.id">
            <div v-if="partsForm && partsForm.createdAt">
              Luotu:
              {{ $formatDateAndHourMinute(partsForm.createdAt) }} -
              <span v-if="partsForm.createdBy">{{
                partsForm.createdBy.user
              }}</span>
            </div>
            <div v-if="partsForm && partsForm.updatedAt">
              Päivitetty:
              {{ $formatDateAndHourMinute(partsForm.updatedAt) }} -
              <span v-if="partsForm.updatedBy">{{
                partsForm.updatedBy.user
              }}</span>
            </div>
            <v-divider v-if="partsForm.id" />
          </v-col>
          <v-col cols="12" v-if="partsForm">
            <div v-for="(ep, i) in partsForm.form" :key="i">
              <v-divider v-if="ep.type === 'title' && i !== 0" class="mt-6" />
              <v-row>
                <template v-if="ep.type === 'title'">
                  <v-col
                    cols="12"
                    class="align-center justify-space-between mt-5 border-bottom"
                  >
                    <span class="text-subtitle-1 font-weight-bold">{{
                      ep.title
                    }}</span>
                  </v-col>
                </template>
                <template v-else-if="ep.type === 'approval'">
                  <v-col cols="12" class="align-center justify-space-between">
                    <span class="text-subtitle-1 font-weight-bold">{{
                      ep.title
                    }}</span>
                    <v-radio-group
                      v-model="ep.value"
                      row
                      :disabled="!hasEditPermissions"
                      class="mt-3"
                    >
                      <v-radio
                        label="Kyllä"
                        :value="true"
                        color="success"
                      ></v-radio>
                      <v-radio
                        label="Ei"
                        :value="false"
                        color="error"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </template>
                <template v-else-if="ep.type === 'textarea'">
                  <v-col cols="12" class="mt-3">
                    <v-textarea
                      outlined
                      auto-grow
                      :label="ep.title"
                      v-model="ep.value"
                      :disabled="!hasEditPermissions"
                    />
                  </v-col>
                </template>
                <template v-else>
                  <v-col
                    cols="9"
                    :class="[
                      'd-flex align-center justify-space-between py-1',
                      ep.done === 2 && 'green lighten-5 green--text',
                      ep.done === 1 && 'orange lighten-5 orange--text',
                      ep.done === 0 && 'red lighten-5 red--text',
                      'border-bottom',
                      'mt-3',
                    ]"
                  >
                    <span class="text-subtitle-2">
                      {{ ep.title }}
                      <v-icon
                        v-if="ep.done === 2"
                        class="green lighten-5 green--text"
                        >mdi mdi-check</v-icon
                      >
                      <v-icon
                        v-if="ep.done === 1"
                        class="orange lighten-5 orange--text"
                        >mdi mdi-exclamation-thick</v-icon
                      >
                      <v-icon
                        v-if="ep.done === 0"
                        class="red lighten-5 red--text"
                        >mdi mdi-alert</v-icon
                      >
                    </span>

                    <span class="caption" v-if="ep.description">{{
                      ep.description
                    }}</span>
                  </v-col>
                  <v-col
                    cols="3"
                    :class="[
                      'd-flex align-center py-1',
                      ep.done === 2 && 'green lighten-5 green--text',
                      ep.done === 1 && 'orange lighten-5 orange--text',
                      ep.done === 0 && 'red lighten-5 red--text',
                      'border-bottom',
                      'justify-end',
                      'mt-3',
                    ]"
                  >
                    <v-btn
                      icon
                      @click="() => (ep.done = 2)"
                      v-if="!ep.done && ep.done !== 0"
                      :disabled="!hasEditPermissions"
                    >
                      <v-icon>mdi mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      @click="() => (ep.done = 1)"
                      :disabled="!hasEditPermissions"
                      v-if="ep.done === 2"
                    >
                      <v-icon>mdi mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      @click="() => (ep.done = 0)"
                      v-if="ep.done === 1"
                      :disabled="!hasEditPermissions"
                    >
                      <v-icon>mdi mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      @click="() => (ep.done = null)"
                      v-if="ep.done === 0"
                      :disabled="!hasEditPermissions"
                    >
                      <v-icon>mdi mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </template>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          v-if="partsForm?.id && hasEditPermissions"
          :loading="loadingDelete"
          color="error"
          @click="deleteForm"
          >Poista
        </v-btn>
        <v-spacer />
        <v-btn color="error" text @click="innerValue = false"> Sulje</v-btn>

        <v-btn
          color="primary"
          @click="saveForm"
          :loading="loadingSave"
          v-if="hasEditPermissions"
          >Tallenna</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";
import ModalPicker from "@/components/ModalPicker.vue";
import jsPDF from "jspdf";
import QRCodeStyling from "qr-code-styling";
import dayjs from "dayjs";

export default {
  name: "NostoapuvalineetHuoltoDialog",
  components: { ModalPicker },
  props: {},
  data() {
    return {
      innerValue: false,
      loadingDelete: false,
      loadingSave: false,
      loadingPrint: false,
      loadingEquipments: false,
      equipmentList: [],
      partsForm: {
        createdAt: null,
        createdBy: null,
        equipment: null,
        date: null,
        inspector: null,
        worksite: null,
        form: [],
      },
      defaultForm: [
        {
          title: "1. Yleinen tarkastus",
          type: "title",
          done: false,
          description: null,
        },
        {
          title:
            "Nostoapuväline on tunnistettavissa (tunnistenumero, tyyppikilpi)",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Tarkastuspäivämäärä ja seuraava tarkastus merkitty",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Käyttöohjeet saatavilla tarvittaessa",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "2. Visuaalinen kunto",
          type: "title",
          done: false,
          description: null,
        },
        {
          title: "Ei näkyviä vaurioita (halkeamia, kulumia, ruostetta)",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Ei epämuodostumia (taipumia, vääntymiä)",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Ei hitsausvaurioita tai korjauksia ilman hyväksyntää",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Ei epäilyttäviä ääniä tai liikkeitä käytön aikana",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "3. Ketjut, vaijerit ja liinat",
          type: "title",
          done: false,
          description: null,
        },
        {
          title: "Ei rispaantumista, katkenneita säikeitä tai litistymiä",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Ketjuissa ei ole venymiä tai epämuodostumia",
          type: null,
          done: null,
          description: null,
        },
        {
          title:
            "Liinoissa ei ole leikkauksia, palovaurioita tai kemiallisia vaurioita",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Oikeat merkinnät (kantavuus, pituus, valmistaja)",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "4. Koukut, sakkelit ja lenkit",
          type: "title",
          done: false,
          description: null,
        },
        {
          title: "Koukut eivät ole vääntyneet tai kuluneet",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Turvalukot toimivat moitteettomasti",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Sakkelit ja lenkit ovat ehjiä ja oikean kokoisia",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Kierrekappaleet liikkuvat sujuvasti",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "5. Toiminnallinen tarkastus (jos soveltuu)",
          type: "title",
          done: false,
          description: null,
        },
        {
          title: "Liikkuvat osat liikkuvat esteettä",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Mekanismit (esim. lukot, säätimet) toimivat oikein",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Ei öljyvuotoja tai muita merkkejä mekaanisista vioista",
          type: null,
          done: null,
          description: null,
        },
        {
          title: "Huomiot ja toimenpiteet",
          type: "textarea",
          value: null,
        },
        {
          title: "Hyväksytty käyttöön",
          type: "approval",
          value: null,
        },
      ],
    };
  },
  computed: {
    hasEditPermissions() {
      return (
        !this.partsForm.id ||
        this.$userDBContext.sub === this.partsForm.createdBy.sub ||
        this.$isSuperAdmin
      );
    },
  },
  watch: {
    innerValue: {
      handler(val) {
        if (!val) this.$emit("close", val);
      },
    },
    "partsForm.equipment": {
      handler(newEquipment, oldEquipment) {
        // If equipment changes and we're not loading an existing form, reset the form
        if (newEquipment !== oldEquipment && !this.partsForm.id) {
          // Clear date, inspector, and worksite when equipment changes
          this.partsForm.date = null;
          this.partsForm.inspector = this.$userDBContext?.user || "";
          this.partsForm.worksite = null;
          // Reset form to default state
          this.partsForm.form = JSON.parse(JSON.stringify(this.defaultForm));
        }
      },
    },
  },
  methods: {
    open(partsForm) {
      this.innerValue = true;
      this.fetchEquipments();
      if (partsForm) {
        this.partsForm = partsForm;
      } else {
        this.resetPartsForm();
      }
    },
    resetPartsForm() {
      this.partsForm = {
        createdAt: null,
        createdBy: null,
        equipment: null,
        date: null,
        inspector: this.$userDBContext?.user || "",
        worksite: null,
        form: JSON.parse(JSON.stringify(this.defaultForm)),
      };
    },
    async fetchEquipments() {
      this.loadingEquipments = true;
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "kalusto"),
            where("deleted", "==", false),
            where("nostoapuvaline", "==", true)
          )
        );
        this.equipmentList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          displayName: `${doc.id} - ${doc.data().name}`,
        }));
      } catch (e) {
        console.error(e);
        this.$toast("Virhe haettaessa kalustoa", {
          type: "error",
        });
      }
      this.loadingEquipments = false;
    },
    async saveForm() {
      if (!this.partsForm.equipment) {
        this.$toast("Kalusto on pakollinen", {
          type: "error",
        });
        return;
      }
      if (!this.partsForm.date) {
        this.$toast("Päivämäärä on pakollinen", {
          type: "error",
        });
        return;
      }
      if (!this.partsForm.inspector) {
        this.$toast("Tarkastaja on pakollinen", {
          type: "error",
        });
        return;
      }

      const approvalField = this.partsForm.form.find(
        (e) => e.type === "approval"
      );
      if (approvalField && approvalField.value === null) {
        this.$toast("Hyväksyntä on pakollinen", {
          type: "error",
        });
        return;
      }

      this.loadingSave = true;
      try {
        if (this.partsForm?.id) {
          // update
          if (!this.hasEditPermissions) {
            this.$toast(
              "Et voi muokata toisen käyttäjän tallentamaa tarkastusmerkintää",
              {
                type: "error",
              }
            );
            this.loadingSave = false;
            return;
          }
          await updateDoc(
            doc(db, "nostoapuvalineethuolto", this.partsForm.id),
            {
              ...this.partsForm,
              updatedAt: Date.now(),
              updatedBy: this.$userDBContext,
              deleted: false,
            }
          );
        } else {
          await addDoc(collection(db, "nostoapuvalineethuolto"), {
            ...this.partsForm,
            createdBy: this.$userDBContext,
            createdAt: Date.now(),
            deleted: false,
          });
        }
        this.$toast(
          "Nostoapuvälineiden tarkastusmerkinnän tallennus onnistui",
          {
            type: "success",
          }
        );
        this.innerValue = false;
        this.partsForm = null;
        this.resetPartsForm();
      } catch (e) {
        this.$toast(
          "Nostoapuvälineiden tarkastusmerkinnän tallennus epäonnistui",
          {
            type: "error",
          }
        );
        console.error(e);
      }

      this.loadingSave = false;
    },
    async deleteForm() {
      if (!this.hasEditPermissions) {
        this.$toast(
          "Et voi poistaa toisen käyttäjän tallentamaa tarkastusmerkintää",
          {
            type: "error",
          }
        );
        return;
      }
      if (
        !(await this.$deleteConfirm(
          "Haluatko varmasti poistaa tarkastusmerkinnän?"
        ))
      ) {
        return;
      }
      this.loadingDelete = true;
      try {
        await updateDoc(doc(db, "nostoapuvalineethuolto", this.partsForm.id), {
          deleted: true,
          deletedAt: Date.now(),
          deletedBy: this.$userDBContext,
        });
        this.innerValue = false;
        this.partsForm = null;
        this.resetPartsForm();
      } catch (e) {
        console.error(e);
        this.$toast("Virhe poistettaessa", { type: "error" });
      }
      this.loadingDelete = false;
    },
    async printInspectionTag() {
      if (!this.partsForm.equipment || !this.partsForm.date) {
        this.$toast("Kalusto ja päivämäärä vaaditaan", { type: "error" });
        return;
      }

      this.loadingPrint = true;
      try {
        const equipment = this.partsForm.equipment;
        const inspectionDate = dayjs(this.partsForm.date);
        const nextInspectionDate = inspectionDate.add(1, "year"); // Use equipment's fullTree for location information
        let locationInfo = "Ei sijaintia";
        if (equipment._fullTree) {
          const locationParts = equipment._fullTree
            .split(" -> ")
            .filter((part) => part.trim());
          // If there are multiple parts and one is "Noormarkun paloasema", remove it
          if (
            locationParts.length > 1 &&
            locationParts.includes("Noormarkun paloasema")
          ) {
            locationInfo = locationParts
              .filter((part) => part !== "Noormarkun paloasema")
              .join(" -> ");
          } else {
            locationInfo = equipment._fullTree;
          }
        } else if (equipment.location) {
          locationInfo = equipment.location;
        }

        // Create larger format PDF (approximately 8cm x 5cm)
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "cm",
          format: [8, 5],
        }); // Generate QR code with equipment ID only
        const qrData = equipment.id;
        const qrOptions = {
          width: 120,
          height: 120,
          type: "svg",
          data: qrData,
          margin: 1,
          qrOptions: {
            typeNumber: 0,
            mode: "Byte",
            errorCorrectionLevel: "L", // Lower error correction for more data capacity
          },
          dotsOptions: {
            color: "#000000",
            type: "square",
          },
          backgroundOptions: {
            color: "#ffffff",
          },
          cornersSquareOptions: {
            color: "#000000",
            type: "square",
          },
          cornersDotOptions: {
            color: "#000000",
            type: "square",
          },
        };
        const qrCode = new QRCodeStyling(qrOptions);
        const qrImage = await qrCode.getRawData("png");
        const arrayBuffer = await qrImage.arrayBuffer();

        // Add professional styling elements
        // Outer border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.04);
        doc.rect(0.1, 0.1, 7.8, 4.8); // Header section with background
        doc.setFillColor(240, 240, 240); // Light gray background
        doc.rect(0.1, 0.1, 7.8, 0.7, "F"); // Fill rectangle
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.02);
        doc.line(0.1, 0.8, 7.9, 0.8); // Header separator line// Header text
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("TARKASTUSTARRA", 0.3, 0.5);

        // Organization name in header
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text("Noormarkun VPK", 0.3, 0.75); // Information section border
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.015);
        doc.rect(0.2, 1.1, 5.1, 1.4);

        // Main information section
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("TARKASTUSTIEDOT", 0.3, 1.3);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`ID: ${equipment.id}`, 0.3, 1.55);
        doc.text(
          `Tarkastettu: ${this.$formatDate(inspectionDate.toDate())}`,
          0.3,
          1.8
        );
        doc.text(
          `Seuraava: ${this.$formatDate(nextInspectionDate.toDate())}`,
          0.3,
          2.05
        );
        doc.text(`Tarkastaja: ${this.partsForm.inspector}`, 0.3, 2.3);

        // QR code with border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.02);
        doc.rect(5.4, 0.15, 2.4, 2.4); // QR code border
        doc.addImage({
          imageData: new Uint8Array(arrayBuffer),
          x: 5.5,
          y: 0.25,
          width: 2.2,
          height: 2.2,
        });

        // QR code label
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text("Skannaa QR-koodi", 5.5, 2.8, { align: "left" }); // Equipment section separator
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.015);
        doc.line(0.2, 2.6, 7.7, 2.6); // Separator line

        // Equipment section header
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("KALUSTO", 0.3, 2.8); // Equipment name with better styling
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        const equipmentName =
          equipment.name.length > 60
            ? equipment.name.substring(0, 57) + "..."
            : equipment.name;
        doc.text(equipmentName, 0.3, 3.05); // Location section with improved styling
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const maxWidth = 4.8; // Maximum width in cm for location text
        const locationText = `Sijainti: ${locationInfo}`;
        const wrappedLocationLines = doc.splitTextToSize(
          locationText,
          maxWidth
        );
        let locationY = 3.4;
        wrappedLocationLines.forEach((line, index) => {
          doc.text(line, 0.3, locationY + index * 0.3);
        });

        // Simple zip tie hole without label
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.03);
        doc.circle(7.3, 4.5, 0.15); // Save the PDF
        doc.save(
          `tarkastustarra_${equipment.id}_${this.$formatDate(
            inspectionDate.toDate()
          )}.pdf`
        );

        this.$toast("Tarkastustarra luotu onnistuneesti", { type: "success" });
      } catch (error) {
        console.error("Error generating inspection tag:", error);
        this.$toast("Virhe tarkastustagin luomisessa", { type: "error" });
      }
      this.loadingPrint = false;
    },
  },
};
</script>

<style scoped lang="scss"></style>
